import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as ReconnectIcon } from '@mui/icons-material';
import { borderRadius, getModernButton } from '../../../theme/theme';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import {
  OperationProgressPanel,
  type OperationProgressMetric,
  type OperationProgressModel,
} from '../../../shared/ui/progress';
import ValidationMarginControl from './ValidationMarginControl';

interface ValidationDialogSettings {
  reconnectGapMarginMillimeters: number;
  defaultReconnectGapMarginMillimeters: number;
  minReconnectGapMarginMillimeters: number;
  maxReconnectGapMarginMillimeters: number;
}

interface ReconnectionDialogProps {
  open: boolean;
  onClose: () => void;
  result: any;
  validationSettings?: ValidationDialogSettings;
  validationSettingsSaving?: boolean;
  onUpdateReconnectGapMargin?: (marginMillimeters: number) => Promise<void> | void;
}

function humanizeValidationGate(value?: string): string {
  switch (value) {
    case 'validation_1_and_22_scoped':
      return 'Validation #1 + #22 (touched scope)';
    case 'validation_1_22_28_scoped':
      return 'Validation #1 + #22 + #28 (touched scope)';
    default:
      return value || 'Validation-gated reconnect';
  }
}

function humanizeFailureKind(value?: string): string | null {
  switch (value) {
    case 'none':
      return null;
    case 'connector_breaks_remain':
      return 'Connector breaks remain';
    case 'system_still_split':
      return 'Affected systems are still split';
    case 'connector_breaks_and_system_still_split':
      return 'Connector breaks remain and affected systems are still split';
    case 'geometry_overlap_remains':
      return 'Geometric overlaps remain';
    case 'geometry_overlap_and_connectivity_remain':
      return 'Geometric overlaps and connectivity issues remain';
    case 'validation_rerun_failed':
      return 'Validation rerun failed';
    case 'reconnect_operation_failed':
      return 'Reconnect operation failed';
    case 'no_reconnectable_issues':
      return 'No reconnectable issues found';
    default:
      return value || null;
  }
}

function formatTimestamp(value?: string): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function toCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function buildReconnectProgressModel(
  result: any,
  rawSummary: any,
  phaseLabel: string
): OperationProgressModel {
  const status = result?.status ?? 'idle';
  const successful = toCount(rawSummary?.successfulReconnections);
  const failed = toCount(rawSummary?.failedReconnections);
  const total = toCount(result?.totalPairs) ?? toCount(rawSummary?.totalPairs);
  const attempted = toCount(result?.attemptedPairCount);
  const processed =
    toCount(result?.processed) ??
    attempted ??
    (successful !== null || failed !== null ? (successful ?? 0) + (failed ?? 0) : null);
  const percent = toCount(result?.percent);
  const isRefreshing = status === 'refreshing' || result?.refreshState === 'running';
  const metrics: OperationProgressMetric[] = [];

  if (total !== null) {
    metrics.push({ label: 'Total pairs', value: String(total) });
  }
  if (attempted !== null) {
    metrics.push({ label: 'Attempted', value: String(attempted), tone: 'info' });
  }
  if (successful !== null) {
    metrics.push({ label: 'Successful', value: String(successful), tone: 'success' });
  }
  if (failed !== null) {
    metrics.push({ label: 'Failed', value: String(failed), tone: failed > 0 ? 'warning' : 'default' });
  }

  return {
    title: result?.operationLabel || 'Reconnect all',
    message: phaseLabel,
    detail: result?.detail || 'Revit is reconnecting selected connector pairs and refreshing affected validation checks.',
    status: status === 'queued' ? 'pending' : status === 'error' ? 'error' : 'running',
    percent,
    processed,
    total,
    phase: isRefreshing ? 'Validation refresh' : status === 'queued' ? 'Queued' : 'Reconnect write',
    state: result?.operationState,
    waitReason: result?.waitReason,
    waitElapsedMs: result?.waitElapsedMs,
    activeElapsedMs: result?.activeElapsedMs,
    elapsedMs: result?.elapsedMs,
    etaMs: result?.etaMs,
    locked: true,
    metrics,
  };
}

export default function ReconnectionDialog({
  open,
  onClose,
  result,
  validationSettings,
  validationSettingsSaving = false,
  onUpdateReconnectGapMargin,
}: ReconnectionDialogProps) {
  const theme = useTheme();
  const buttonStyles = getModernButton(theme);
  const status = result?.status ?? 'idle';
  const isProgressState =
    status === 'queued' ||
    status === 'running' ||
    status === 'refreshing';
  const hasSummary =
    typeof result?.totalPairs === 'number' ||
    typeof result?.successfulReconnections === 'number' ||
    typeof result?.failedReconnections === 'number';
  const phaseLabel = result?.phaseLabel ?? (isProgressState ? 'Running' : 'Completed');
  const startedAtLabel = formatTimestamp(result?.startedAt);
  const completedAtLabel = formatTimestamp(result?.completedAt);
  const refreshedAtLabel = formatTimestamp(result?.refreshedAt);
  const rawSummary = result?.rawReconnectSummary ?? result;
  const hasValidationSummary =
    typeof result?.remainingConnectorBreakIssues === 'number' ||
    typeof result?.remainingSystemBreakIssues === 'number' ||
    typeof result?.remainingGeometryOverlapIssues === 'number' ||
    Array.isArray(result?.touchedSystemNames) ||
    Boolean(result?.failureKind && result.failureKind !== 'none');
  const failureKindLabel = humanizeFailureKind(result?.failureKind);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReconnectIcon color="primary" />
          <Typography variant="h6">
            {isProgressState
              ? result?.operationKind === 'single'
                ? 'Reconnect pair progress'
                : 'Reconnect all progress'
              : 'Reconnection results'}
          </Typography>
          <HelpTooltipTrigger
      label="Reconnection results"
            help={validationActionTooltips.reconnectAll}
            placement="bottom-start"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {result ? (
          <Box>
            {isProgressState ? (
              <OperationProgressPanel
                compact
                model={buildReconnectProgressModel(result, rawSummary, phaseLabel)}
                sx={{ mb: 2 }}
                testId="reconnect-operation-progress"
              />
            ) : null}

            {validationSettings && onUpdateReconnectGapMargin ? (
              <Alert
                severity="info"
                data-testid="reconnect-gap-allowance-editor"
                sx={{ mb: 2, borderRadius: `${borderRadius.md}px` }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.25}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  useFlexGap
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Gap allowance for next validation
                    </Typography>
                    <Typography variant="body2">
                      Current reconnect limit: {validationSettings.reconnectGapMarginMillimeters.toFixed(1)} mm
                    </Typography>
                  </Box>
                  <ValidationMarginControl
                    value={validationSettings.reconnectGapMarginMillimeters}
                    defaultValue={validationSettings.defaultReconnectGapMarginMillimeters}
                    min={validationSettings.minReconnectGapMarginMillimeters}
                    max={validationSettings.maxReconnectGapMarginMillimeters}
                    disabled={isProgressState}
                    saving={validationSettingsSaving}
                    onCommit={onUpdateReconnectGapMargin}
                  />
                </Stack>
              </Alert>
            ) : null}

            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Operation timeline
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {phaseLabel}
              </Typography>
              {startedAtLabel ? (
                <Typography variant="body2">
                  <strong>Started:</strong> {startedAtLabel}
                </Typography>
              ) : null}
              {completedAtLabel ? (
                <Typography variant="body2">
                  <strong>Reconnect finished:</strong> {completedAtLabel}
                </Typography>
              ) : null}
              {result.refreshMessage ? (
                <Typography variant="body2">
                  <strong>Validation refresh:</strong> {result.refreshMessage}
                </Typography>
              ) : null}
              {refreshedAtLabel ? (
                <Typography variant="body2">
                  <strong>Validation refreshed:</strong> {refreshedAtLabel}
                </Typography>
              ) : null}
              {typeof result.pollingAttempt === 'number' ? (
                <Typography variant="body2">
                  <strong>Progress check:</strong> attempt {result.pollingAttempt}
                </Typography>
              ) : null}
            </Box>

            {hasSummary ? (
              <Alert
                severity={rawSummary.failedReconnections === 0 ? 'success' : 'warning'}
                sx={{ mb: 2, borderRadius: `${borderRadius.md}px` }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Reconnect attempt summary
                </Typography>
                <Typography variant="body1">
                  <strong>Attempted pairs:</strong> {result.attemptedPairCount ?? rawSummary.totalPairs}
                </Typography>
                <Typography variant="body1">
                  <strong>Total pairs:</strong> {rawSummary.totalPairs}
                </Typography>
                <Typography variant="body1">
                  <strong>Successful:</strong> {rawSummary.successfulReconnections}
                </Typography>
                <Typography variant="body1">
                  <strong>Failed:</strong> {rawSummary.failedReconnections}
                </Typography>
                {result.refreshState === 'running' ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Validation is being rerun now. The visible issue list may still show the pre-reconnect report until refresh completes.
                  </Typography>
                ) : null}
              </Alert>
            ) : null}

            {hasValidationSummary ? (
              <Alert
                severity={
                  result.remainingConnectorBreakIssues === 0 &&
                  result.remainingSystemBreakIssues === 0 &&
                  (result.remainingGeometryOverlapIssues ?? 0) === 0 &&
                  !failureKindLabel
                    ? 'success'
                    : 'error'
                }
                sx={{ mb: 2, borderRadius: `${borderRadius.md}px` }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Validation gate summary
                </Typography>
                <Typography variant="body1">
                  <strong>Gate:</strong> {humanizeValidationGate(result.validationGate)}
                </Typography>
                <Typography variant="body1">
                  <strong>Remaining connector breaks:</strong> {result.remainingConnectorBreakIssues ?? 0}
                </Typography>
                <Typography variant="body1">
                  <strong>Remaining system breaks:</strong> {result.remainingSystemBreakIssues ?? 0}
                </Typography>
                <Typography variant="body1">
                  <strong>Remaining geometric overlaps:</strong> {result.remainingGeometryOverlapIssues ?? 0}
                </Typography>
                {failureKindLabel ? (
                  <Typography variant="body1">
                    <strong>Failure kind:</strong> {failureKindLabel}
                  </Typography>
                ) : null}
                {Array.isArray(result.touchedSystemNames) && result.touchedSystemNames.length > 0 ? (
                  <Typography variant="body1">
                    <strong>Affected systems:</strong> {result.touchedSystemNames.join(', ')}
                  </Typography>
                ) : null}
              </Alert>
            ) : null}

            {rawSummary.successMessages && rawSummary.successMessages.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Successful reconnections:
                </Typography>
                <List dense>
                  {rawSummary.successMessages.map((msg: string, idx: number) => (
                    <ListItem key={idx}>
                      <ListItemText primary={msg} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {rawSummary.errorMessages && rawSummary.errorMessages.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="error.main" gutterBottom>
                  Failed reconnections:
                </Typography>
                <List dense>
                  {rawSummary.errorMessages.map((msg: string, idx: number) => (
                    <ListItem key={idx}>
                      <ListItemText primary={msg} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          disabled={isProgressState}
          sx={{
            ...buttonStyles.contained,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
